// @flow

import React, { Component } from "react";
import { Map, TileLayer, Marker, Popup, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./styles.css";
import L from "leaflet";
import townPolys from "../../data/vt_town_polygons.geo.json";
import CheckBox from "../checkbox";
import { flatten } from "ramda";

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png")
});


const dropOffIcon = L.icon({
    iconUrl: require("./assets/drop-off-marker.png"),
    shadowUrl: require("./assets/marker-shadow.png"),
    iconSize: [35, 40], // size of the icon
    shadowSize: [43, 31], // size of the shadow
    iconAnchor: [23, 40], // point of the icon which will correspond to marker's location
    shadowAnchor: [2, 30], // the same for the shadow
    popupAnchor: [0, -40] // point from which the popup should open relative to the iconAnchor
});

const trashDropIcon = L.icon({
    iconUrl: require("./assets/trash-marker.png"),
    shadowUrl: require("./assets/marker-shadow.png"),
    iconSize: [35, 40], // size of the icon
    shadowSize: [43, 31], // size of the shadow
    iconAnchor: [17, 40], // point of the icon which will correspond to marker's location
    shadowAnchor: [2, 30], // the same for the shadow
    popupAnchor: [0, -40] // point from which the popup should open relative to the iconAnchor
});

const pickUpIcon = L.icon({
    iconUrl: require("./assets/pick-up-marker.png"),
    shadowUrl: require("./assets/marker-shadow.png"),
    iconSize: [35, 40], // size of the icon
    shadowSize: [43, 31], // size of the shadow
    iconAnchor: [17, 40], // point of the icon which will correspond to marker's location
    shadowAnchor: [2, 30], // the same for the shadow
    popupAnchor: [0, -40] // point from which the popup should open relative to the iconAnchor
});

const trashCollectedIcon = L.icon({
    iconUrl: require("./assets/trash-collected-marker.png"),
    shadowUrl: require("./assets/marker-shadow.png"),
    iconSize: [35, 40], // size of the icon
    shadowSize: [43, 31], // size of the shadow
    iconAnchor: [17, 40], // point of the icon which will correspond to marker's location
    shadowAnchor: [2, 30], // the same for the shadow
    popupAnchor: [0, -40] // point from which the popup should open relative to the iconAnchor
});

const teamIcon = L.icon({
    iconUrl: require("./assets/team-marker.png"),
    shadowUrl: require("./assets/marker-shadow.png"),
    iconSize: [35, 40], // size of the icon
    shadowSize: [43, 31], // size of the shadow
    iconAnchor: [17, 40], // point of the icon which will correspond to marker's location
    shadowAnchor: [2, 30], // the same for the shadow
    popupAnchor: [0, -40] // point from which the popup should open relative to the iconAnchor
});

type State = {
    lat: number,
    lng: number,
    zoom: number
};

type Props = {
    trashDrops: ?Object,
    pickupLocations: ?Array<Object>,
    dropOffLocations: ?Array<Object>,
    teams: Object
};

export default class TrashMap extends Component<Props, State> {
    state = {
        isExpanded: false,
        lat: 44,
        lng: -72.5890947,
        zoom: 8,
        showTowns: false,
        showTrashDrops: true,
        showCollectedTrashDrops: false,
        showPickups: false,
        showDropOffs: false,
        showTeams: false
    };

    render() {
        const position = [this.state.lat, this.state.lng];
        const { showDropOffs, showPickups, showTowns, showTrashDrops, showTeams, showCollectedTrashDrops } = this.state;
        const { pickupLocations, dropOffLocations, trashDrops, teams } = this.props;
        const trashDropMarkers = Object.values(trashDrops || {})
            .filter(drop => !(drop || {}).wasCollected)
            .map(drop => {
                const location = { lat: ((drop || {}).location || {}).latitude, lng: ((drop || {}).location || {}).longitude };
                return (location.lat && location.lng) ? (
                    <Marker icon={ trashDropIcon }
                        key={ (drop || {}).id }
                        position={ location }
                    >
                        <Popup>
                            {`${(drop || {}).bagCount || "0"} bags`}<br/>{((drop || {}).tags || []).join(" ")}
                        </Popup>
                    </Marker>
                ) : null;
            });

        const collectedTrashDropMarkers = Object.values(trashDrops)
            .filter(drop => (drop || {}).wasCollected)
            .map(drop => {
                const location = { lat: ((drop || {}).location || {}).latitude, lng: ((drop || {}).location || {}).longitude };
                return (location.lat && location.lng) ? (
                    <Marker icon={ trashCollectedIcon }
                        key={ (drop || {}).id }
                        position={ location }
                    >
                        <Popup>
                            {`${(drop || {}).bagCount || "0"} bags`}<br/>{((drop || {}).tags || []).join(" ")}
                        </Popup>
                    </Marker>
                ) : null;
            });


        const dropOffMarkers = (dropOffLocations || []).map((loc, index) => {
            const location = { lat: ((loc || {}).coordinates || {}).latitude, lng: ((loc || {}).coordinates || {}).longitude };
            return (location.lat && location.lng) ? (
                <Marker icon={ dropOffIcon }
                    key={ index }
                    position={ location }
                >
                    <Popup>
                        {(loc || {}).name}<br/>{(loc || {}).address}
                    </Popup>
                </Marker>
            ) : null;
        });
        const pickupMarkers = (pickupLocations || []).map((loc, index) => {
            const location = { lat: ((loc || {}).coordinates || {}).latitude, lng: ((loc || {}).coordinates || {}).longitude };
            return (location.lat && location.lng) ? (
                <Marker icon={ pickUpIcon }
                    key={ index }
                    position={ location }
                >
                    <Popup>
                        {(loc || {}).name}<br/>{(loc || {}).address}
                    </Popup>
                </Marker>
            ) : null;
        });

        const teamMarkers = flatten(Object.values(teams || {})
            .map(team => (team.locations || [])
                .map(_loc => ({ ..._loc, name: team.name }))))
            .map(loc => {
                const location = { lat: ((loc || {}).coordinates || {}).latitude, lng: ((loc || {}).coordinates || {}).longitude };
                return (location.lat && location.lng) ? (
                    <Marker icon={ teamIcon }
                        key={ (loc || {}).id }
                        position={ location }
                    >
                        <Popup>
                            {(loc || {}).name}<br/>{(loc || {}).address}
                        </Popup>
                    </Marker>
                ) : null;
            });

        const showTownLabels = false;

        return (
            <div className={ "trash-map" }>
                <div className={ "trash-map--wrapper" }>
                    <Map center={ position } zoom={ this.state.zoom }>
                        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}.png"/>
                        {showTowns
                            ? (
                                <GeoJSON data={ townPolys }
                                    onEachFeature={ (feature, layer) => {
                                        layer.bindTooltip(feature.properties.TOWNNAMEMC, { permanent: showTownLabels });
                                    } }
                                />
                            )
                            : null}
                        {showCollectedTrashDrops ? collectedTrashDropMarkers : null}
                        {showTrashDrops ? trashDropMarkers : null}
                        {showPickups ? pickupMarkers : null}
                        {showDropOffs ? dropOffMarkers : null}
                        {showTeams ? teamMarkers : null}
                        {showCollectedTrashDrops ? collectedTrashDropMarkers : null}
                    </Map>
                    <div className={ `trash-map--controls${this.state.isExpanded ? " is-expanded" : ""}` }>
                        <button
                            className={ "trash-map--expand-button" }
                            onClick={ () => {
                                this.setState({ isExpanded: !this.state.isExpanded });
                            } }/>
                        <div className={ "trash-map--controls-wrapper" }>
                            <CheckBox checked={ this.state.showTowns }
                                labelText={ "Show Towns" }
                                onChange={ () => {
                                    this.setState({ showTowns: !this.state.showTowns });
                                } }/>
                            <CheckBox checked={ this.state.showTrashDrops }
                                labelText={ "Show Uncollected TrashDrops" }
                                onChange={ () => {
                                    this.setState({ showTrashDrops: !this.state.showTrashDrops });
                                } }/>
                            <CheckBox checked={ this.state.showCollectedTrashDrops }
                                labelText={ "Show Collected TrashDrops" }
                                onChange={ () => {
                                    this.setState({ showCollectedTrashDrops: !this.state.showCollectedTrashDrops });
                                } }/>
                            <CheckBox checked={ this.state.showPickups }
                                labelText={ "Show Pickup Locations" }
                                onChange={ () => {
                                    this.setState({ showPickups: !this.state.showPickups });
                                } }/>
                            <CheckBox checked={ this.state.showDropOffs }
                                labelText={ "Show Drop Off Locations" }
                                onChange={ () => {
                                    this.setState({ showDropOffs: !this.state.showDropOffs });
                                } }/>
                            <CheckBox checked={ this.state.showTeams }
                                labelText={ "Show Team Areas" }
                                onChange={ () => {
                                    this.setState({ showTeams: !this.state.showTeams });
                                } }/>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
